import { Engine } from "@courses-pm2/api/src/ts/utils";

import { parseCaptionedContentElement } from "../parseCaptionedContentElement";
import {
  GlossaryCollectionModel,
  GlossaryCollectionWordModel,
} from "../../models/elements/GlossaryCollectionModel";

export async function parseGlossaryCollection({
  node,
  portalId,
  engine,
}: {
  node: Element;
  portalId: string;
  engine?: Engine;
}): Promise<GlossaryCollectionModel | undefined> {
  // Announce that a model has been identified if possible
  if (engine) {
    engine.announce(GlossaryCollectionModel);
  }

  const baseData = parseCaptionedContentElement(node);
  const title = node.getAttribute("title") ?? "";
  const description = node.getAttribute("description") ?? null;

  const itemNodes = Array.from(node.childNodes).find(
    guardChildNodeByType("Items"),
  );

  if (!itemNodes) {
    return undefined;
  }

  const glossaryWords = Array.from(itemNodes.children)
    .map((it) => parseGlossaryItem(it))
    .filter((it): it is GlossaryCollectionWordModel => it !== null)
    .sort((a, b) => {
      const wordA = a.term?.toString();
      const wordB = b.term?.toString();

      if (wordA === undefined || wordB === undefined) {
        return 0;
      }

      return wordA.localeCompare(wordB, "da-DK", { numeric: true });
    });

  if (glossaryWords.length === 0) {
    return undefined;
  }

  return new GlossaryCollectionModel({
    engine,
    portalId,

    ...baseData,

    title,
    description,

    glossaryWords,

    options: {
      layout: extractLayout(node.getAttribute("elementBoxType")),
      withGraphic: node.getAttribute("graphics") === "true",
    },
  });
}

function extractLayout(
  layout: string | null,
): "box-1" | "box-2" | "box-3" | undefined {
  switch (layout) {
    case "bokstype-1":
      return "box-1";

    case "bokstype-2":
      return "box-2";

    case "bokstype-3":
      return "box-3";

    default:
      return undefined;
  }
}

function parseGlossaryItem(
  itemNode: Element,
): GlossaryCollectionWordModel | null {
  const glossaryPageNode = itemNode.querySelector("GlossaryPage");

  return {
    id: itemNode.getAttribute("id") ?? "",
    term: itemNode.getAttribute("term") ?? "",
    definition:
      removeCDataTag(itemNode?.querySelector("Definition")?.innerHTML) ?? "",

    page: glossaryPageNode
      ? {
          id: glossaryPageNode.getAttribute("glossaryPageId") ?? "",
          title:
            glossaryPageNode.getAttribute("glossaryPageTitle") ?? undefined,
        }
      : undefined,
  };
}

function removeCDataTag(input: string | undefined): string | undefined {
  return input?.replace("<![CDATA[", "").replace("]]>", "");
}

function guardChildNodeByType(type: string): (it: ChildNode) => it is Element {
  return (it: ChildNode): it is Element => {
    return (
      "tagName" in it &&
      typeof it.tagName === "string" &&
      it.tagName.toLowerCase() === type.toLowerCase()
    );
  };
}
